import {HttpContext} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {Observable} from 'rxjs';
import {TokenDataProviderService} from '../../core/services/token-data-provider.service';
import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {plainToClass, plainToClassFromExist} from 'class-transformer';
import {map} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {MessageApiModel} from '../models/message.api.model';
import {UserPasswordApiModel} from '../models/user-password.api-model';
import {ResultListApiModel} from '../models/result-list.api.model';
import {QueryParamsApiModel} from '../models/query-params-api.model';
import {QueryParamsService} from '../../core/services/query-params.service';
import {UserApiModel} from '../models/user.api-model';
import {UserStatisticsApiModel} from '../models/user-statistics.api-model';
import {ReactionApiModel} from "../models/reaction.api.model";
import {FollowApiModel} from "../models/follow.api-model";
import {MarketplaceAdvertisementApiModel} from "../models/marketplace-advertisement.api.model";
import {ParticipantApiModel} from "../models/participant.api.model";
import {RankingUserApiModel} from "../../features/ranking/ranking-user.api-model";


@Injectable({
  providedIn: 'root'
})
export class UserRestService {
  public readonly resource: string = '/users';

  public user: UserApiModel;

  constructor(
    private http: HttpClient,
    public ts: TokenDataProviderService,
    private params: QueryParamsService
  ) {
      this.user = this.ts.getJwtUser();
  }

  public getCollection(queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel(), groups: string[] = []): Observable<ResultListApiModel<UserApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<UserApiModel>>(environment.apiUrl + this.resource, {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<UserApiModel>(UserApiModel), result as object, {groups})));
  }

  public getRankingCollection(queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<RankingUserApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<RankingUserApiModel>>(environment.apiUrl + '/rankings/users', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<RankingUserApiModel>(RankingUserApiModel), result as object)));
  }

  public getOnlineUsers(): Observable<ResultListApiModel<UserApiModel>> {
    const headers = new HttpHeaders({'x-cache-bypass': 'true'});
    return this.http.get<{records: UserApiModel[]; total: number}>(environment.apiUrl + this.resource + '/online', {headers})
      .pipe(map(result => {
        const collection = new ResultListApiModel<UserApiModel>(UserApiModel);
        collection.records = result.records;
        collection.total = result.total;
        return collection;
      }));
  }

  public findInvitationCandidate(email: string): Observable<UserApiModel | null> {
    const params = new HttpParams().set('email', email);
    return this.http.get<{candidate: UserApiModel | null}>(environment.apiUrl + this.resource + '/invitation-candidate', {params})
      .pipe(map(result => result.candidate));
  }

  public get(id: string, options?: {headers?: HttpHeaders; context?: HttpContext}): Observable<UserApiModel> {
    return this.http.get<UserApiModel>(environment.apiUrl + id, options)
      .pipe(map((successResponse) => plainToClass(UserApiModel, successResponse)));
  }

  public getAdmin(id: string): Observable<UserApiModel> {
    return this.http.get<UserApiModel>(environment.apiUrl + id + '/admin')
      .pipe(map((successResponse) => plainToClass(UserApiModel, successResponse, {groups: ['admin:user-read']})));
  }

  public getStatistics(id: string): Observable<UserStatisticsApiModel> {
    return this.http.get<UserStatisticsApiModel>(environment.apiUrl + id + '/statistics')
      .pipe(map((successResponse) => plainToClass(UserStatisticsApiModel, successResponse)));
  }

  public put(id: string, userApiModel: UserApiModel): Observable<UserApiModel> {
    return this.http.put<UserApiModel>(environment.apiUrl + id, userApiModel)
      .pipe(map((successResponse: UserApiModel) => plainToClass(UserApiModel, successResponse)));
  }

  public putAdmin(id: string, userApiModel: UserApiModel): Observable<UserApiModel> {
    return this.http.put<UserApiModel>(environment.apiUrl + id + '/admin', userApiModel)
      .pipe(map((successResponse: UserApiModel) => plainToClass(UserApiModel, successResponse, {groups: ['admin:user-read']})));
  }

  public putSocialLinks(id: string, userApiModel: UserApiModel): Observable<UserApiModel> {
    return this.http.put<UserApiModel>(environment.apiUrl + id + '/social-profile-links', userApiModel)
      .pipe(map((successResponse: UserApiModel) => plainToClass(UserApiModel, successResponse)));
  }

  public startInstagramConnect(): Observable<{authorizationUrl: string}> {
    return this.http.post<{authorizationUrl: string}>(`${environment.apiUrl}/users/me/instagram/connect/start`, {});
  }

  public completeInstagramConnect(code: string, state: string): Observable<{status: string; username: string; accountType: string}> {
    return this.http.get<{status: string; username: string; accountType: string}>(`${environment.apiUrl}/users/me/instagram/connect/callback`, {params: {code, state}});
  }

  public putSettings(id: string, userApiModel: UserApiModel): Observable<UserApiModel> {
    return this.http.put<UserApiModel>(environment.apiUrl + id + '/settings', userApiModel)
      .pipe(map((successResponse: UserApiModel) => plainToClass(UserApiModel, successResponse)));
  }

  public patch(id: string, userApiModel: UserApiModel): Observable<UserApiModel> {
    return this.http.patch<UserApiModel>(environment.apiUrl + id, userApiModel)
      .pipe(map((successResponse: UserApiModel) => plainToClass(UserApiModel, successResponse)));
  }

  public changePassword(userApiModel: UserPasswordApiModel): Observable<MessageApiModel> {
    return this.http.post(environment.apiUrl + `/users/password-change`, userApiModel).pipe(map(
      successResponse => plainToClass(MessageApiModel, successResponse)));
  }

  public deleteAccount(password: string): Observable<MessageApiModel> {
    return this.http.post<MessageApiModel>(environment.apiUrl + `${this.resource}/delete-account`, {password})
      .pipe(map((successResponse) => plainToClass(MessageApiModel, successResponse)));
  }

  public getReceivedLikesSubResourceCollection(userId: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<ReactionApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<ReactionApiModel>>(environment.apiUrl + userId + '/received_reactions', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<ReactionApiModel>(ReactionApiModel), result as object)));
  }

  public getLikedReactionsSubResourceCollection(userId: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<ReactionApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<ReactionApiModel>>(environment.apiUrl + userId + '/liked_reactions', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<ReactionApiModel>(ReactionApiModel), result as object)));
  }

  public getAggregatedReceivedLikesSubResourceCollection(userId: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<ReactionApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<ReactionApiModel>>(environment.apiUrl + userId + '/aggregated_received_reactions', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<ReactionApiModel>(ReactionApiModel), result as object)));
  }

  public getReceivedFollowsSubResourceCollection(userId: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<FollowApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<FollowApiModel>>(environment.apiUrl + userId + '/received_follows', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<FollowApiModel>(FollowApiModel), result as object)));
  }

  public getAggregatedReceivedFollowsSubResourceCollection(userId: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<FollowApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<FollowApiModel>>(environment.apiUrl + userId + '/aggregated_received_follows', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<FollowApiModel>(FollowApiModel), result as object)));
  }

  public getFollowsSubResourceCollection(userId: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<FollowApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<FollowApiModel>>(environment.apiUrl + userId + '/follows', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<FollowApiModel>(FollowApiModel), result as object)));
  }

  public getShareChatRecipientsSubResourceCollection(
    userId: string,
    options: {q?: string; page?: number; limit?: number} = {}
  ): Observable<ResultListApiModel<UserApiModel>> {
    let params = new HttpParams();

    if (typeof options.q === 'string') {
      params = params.set('q', options.q);
    }
    if (typeof options.page === 'number') {
      params = params.set('page', String(options.page));
    }
    if (typeof options.limit === 'number') {
      params = params.set('limit', String(options.limit));
    }

    return this.http.get<ResultListApiModel<UserApiModel>>(environment.apiUrl + userId + '/share_chat_recipients', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<UserApiModel>(UserApiModel), result as object)));
  }

  public getReactionsSubResourceCollection(userId: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<ReactionApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<ReactionApiModel>>(environment.apiUrl + userId + '/reactions', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<ReactionApiModel>(ReactionApiModel), result as object)));
  }

  public delete(userAM: UserApiModel): Observable<HttpResponse<any>> {
    return this.http.delete<HttpResponse<any>>(environment.apiUrl + userAM['@id']);
  }

  public getMarketplaceAdvertisementsSubResourceCollection(userId: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<MarketplaceAdvertisementApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<MarketplaceAdvertisementApiModel>>(environment.apiUrl + userId + '/marketplace_advertisements', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<MarketplaceAdvertisementApiModel>(MarketplaceAdvertisementApiModel), result as object)));
  }

  public getSavedMarketplaceAdvertisementsSubResourceCollection(
    userId: string,
    queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel(),
    options?: {headers?: HttpHeaders}
  ): Observable<ResultListApiModel<FollowApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<FollowApiModel>>(
      environment.apiUrl + userId + '/saved_marketplace_advertisements',
      {...options, params}
    )
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<FollowApiModel>(FollowApiModel), result as object)));
  }

  public getParticipationsSubResourceCollection(userId: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<ParticipantApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<MarketplaceAdvertisementApiModel>>(environment.apiUrl + userId + '/participations', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<ParticipantApiModel>(ParticipantApiModel), result as object)));
  }
}
